import React from 'react';
import { trpc } from '@org/clients/school';
import { keepPreviousData } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { extractFirstUrl } from './join-utils';
import { CarouselWrapper, EmptyCarouselCard } from './carousel-card';
import { ClassCard } from './class-card';
import type { JoinState } from './join-utils';
import type { TactileVariant } from './tactile-card-styles';
import type { ClassKind } from '../lib/class-kind-filter';

const formatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function ClassCarousel({ kind, search }: { kind: ClassKind | null; search?: string }) {
  // `kind` is null while the student's cohort is still unknown: hold the
  // skeleton instead of firing a query that would be refetched right away.
  // With a keyword, the backend searches all her classes (not just ±30 days);
  // previous cards stay on screen while the next search loads.
  const searchTerm = search?.trim() || undefined;
  const { data: classEvents, isLoading } = trpc.classes.getDashboardClasses.useQuery(
    { kind: kind ?? 'all', search: searchTerm },
    { enabled: kind !== null, placeholderData: keepPreviousData },
  );
  const utils = trpc.useUtils();
  const { mutate: recordVisit } = trpc.classes.recordClassVisit.useMutation({
    onSuccess: () => {
      utils.classes.getDashboardClasses.invalidate();
    },
  });
  const { t } = useTranslation('admin');

  if (isLoading || kind === null) {
    return <Card className="skeleton-card h-64 w-full" />;
  }

  if (!classEvents || classEvents.length === 0) {
    return (
      <EmptyCarouselCard
        eyebrow={t(searchTerm ? 'p2e.classes.page.search.emptyEyebrow' : 'p2e.classes.carousel.empty.eyebrow')}
        title={t(searchTerm ? 'p2e.classes.page.search.emptyTitle' : 'p2e.classes.carousel.empty.title')}
        description={t(
          searchTerm ? 'p2e.classes.page.search.emptyDescription' : 'p2e.classes.carousel.empty.description',
        )}
      />
    );
  }

  // Data is sorted chronologically by the backend: [oldest past] → [today/next] → [future]
  // Center on the first non-past event, or the last card if all past.
  const centerIndex = (() => {
    const idx = classEvents.findIndex((cls) => !cls.isPast || cls.isLive);
    return idx >= 0 ? idx : classEvents.length - 1;
  })();

  return (
    <CarouselWrapper centerIndex={centerIndex}>
      {classEvents.map((cls) => {
        const formattedDate = formatter.format(new Date(cls.eventStartTime));
        const completedBlocks = cls.completedBlocks ?? 0;
        const totalBlocks = cls.totalBlocks ?? 0;
        const hasCompletedBlocks = Number.isFinite(completedBlocks) && completedBlocks > 0;
        const lastBlockIdx = hasCompletedBlocks ? Math.max(completedBlocks - 1, 0) : undefined;
        const linkTo = hasCompletedBlocks ? '/classes/$classId/block/$blockIdx' : '/classes/$classId';
        const linkParams = hasCompletedBlocks
          ? { classId: cls.classId, blockIdx: String(lastBlockIdx) }
          : { classId: cls.classId };

        const joinState: JoinState | null = cls.isLive ? 'live' : cls.isSoon ? 'soon' : null;
        const joinHref = extractFirstUrl(cls.eventLocation);
        const variant: TactileVariant = cls.isToday || cls.isLive ? 'dark' : 'light';
        const badgeLabel = cls.isLive
          ? t('p2e.classes.page.badge.live', { defaultValue: 'Live' })
          : cls.isToday
            ? t('p2e.classes.carousel.badge.today')
            : cls.isPast
              ? t('p2e.classes.carousel.badge.previous', { defaultValue: 'Previous' })
              : t('p2e.classes.carousel.badge.next');

        return (
          <Link
            key={`${cls.classId}-${cls.eventStartTime}`}
            to={linkTo}
            params={linkParams}
            onClick={() => recordVisit({ classId: cls.classId })}
            className="block w-64 sm:w-72 outline-none focus-visible:ring-2 focus-visible:ring-rose-300 rounded-xl"
          >
            <ClassCard
              name={cls.name ?? t('p2e.classes.carousel.fallbackTitle', { defaultValue: 'Class' })}
              subtitle={cls.description}
              formattedDate={formattedDate}
              imageUrl={cls.imageUrl}
              badgeLabel={badgeLabel}
              variant={variant}
              completedBlocks={completedBlocks}
              totalBlocks={totalBlocks}
              joinState={joinState}
              joinHref={joinHref}
            />
          </Link>
        );
      })}
    </CarouselWrapper>
  );
}
